import { DefaultError, useMutation, useQuery } from "@tanstack/react-query";
import { useQueryToast } from "hooks";
import i18next from "i18next";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  ICancelDigitalDelivery,
  ICreateAllDigitalDeliveries,
  ICreateDigitalDelivery,
  IDigitalDeliveryResponse,
  IUnsupportedRolesResponse,
  IUpdateDigitalDelivery,
  IWithdrawDigitalDelivery,
} from "./types";

export const useGetDigitalDelivery = (
  MusicProductId: string,
  SalesChannelId: string,
  ProviderId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDigitalDeliveryResponse, DefaultError>({
    queryKey: ["digital-delivery", MusicProductId, SalesChannelId, ProviderId],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `DigitalDelivery`,
        params: { MusicProductId, SalesChannelId, ProviderId },
      });

      return responseHandler(response, {
        groupName: `useGetDigitalDelivery - ${MusicProductId} - ${SalesChannelId} - ${ProviderId}`,
        notFoundPath: `/music-product-digital/${MusicProductId}/delivery`,
      });
    },
    enabled,
    retry: false,
  });
};

export const usePostDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostDigitalDelivery");
  return useMutation<{}, DefaultError, ICreateDigitalDelivery>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "DigitalDelivery",
        body,
      });

      return responseHandler(response, {
        groupName: "usePostDigitalDelivery",
        notFoundPath: "/music-product-digital",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};
export const usePostAllDigitalDeliveries = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "usePostAllDigitalDeliveries"
  );
  return useMutation<{}, DefaultError, ICreateAllDigitalDeliveries>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "DigitalDelivery/All",
        body,
      });

      return responseHandler(response, {
        groupName: "usePostAllDigitalDeliveries",
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};
export const usePutAllDigitalDeliveries = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "usePutAllDigitalDeliveries"
  );
  return useMutation<{}, DefaultError, ICreateAllDigitalDeliveries>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "DigitalDelivery/All",
        body,
      });

      return responseHandler(response, {
        groupName: "usePutAllDigitalDeliveries",
        notFoundPath: `/music-product-digital/${body.productId}/delivery`,
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePutDigitalDelivery");
  return useMutation<{}, DefaultError, IUpdateDigitalDelivery>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "DigitalDelivery",
        body,
      });

      return responseHandler(response, {
        groupName: "usePutDigitalDelivery",
        notFoundPath: "/music-product-digital",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCancelDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useCancelDigitalDelivery"
  );
  return useMutation<{}, DefaultError, ICancelDigitalDelivery>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.cancel.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "DigitalDelivery/Cancel",
        body,
      });

      return responseHandler(response, {
        groupName: "useCancelDigitalDelivery",
        notFoundPath: "/music-product-digital",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.cancel.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useWithdrawnDigitalDelivery = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useWithdrawnDigitalDelivery"
  );
  return useMutation<{}, DefaultError, IWithdrawDigitalDelivery>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.withdrawn.loading", {
          what: i18next.t("digital-delivery.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "DigitalDelivery/Withdrawn",
        body,
      });

      return responseHandler(response, {
        groupName: "useWithdrawnDigitalDelivery",
        notFoundPath: "/music-product-digital",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.withdrawn.success", {
          what: i18next.t("digital-delivery.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetUnsupportedRoles = (
  digitalMusicProductId: string,
  salesChannelId: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IUnsupportedRolesResponse, DefaultError>({
    queryKey: ["unsupported-roles", digitalMusicProductId, salesChannelId],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "MusicProductDigital/Deliveries/UnsuportedRoles",
        params: { digitalMusicProductId, salesChannelId },
      });

      return responseHandler(response, {
        groupName: `useGetUnsupportedRoles - ${digitalMusicProductId} - ${salesChannelId}`,
        notFoundPath: "/music-product-digital",
      });
    },
    enabled,
    retry: false,
  });
};

export const useGetCheckExternalIds = (productId: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<Array<{ id: string; nickname: string }>, DefaultError>({
    queryKey: ["check-external-ids", productId],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "MusicProductDigital/CheckExternalIds",
        params: { productId },
      });

      return responseHandler(response, {
        groupName: `useGetCheckExternalIds - ${productId}`,
        notFoundPath: "/music-product-digital",
      });
    },
    retry: false,
  });
};
